import { NavigationProp, ParamListBase, useIsFocused, useNavigation } from "@react-navigation/native";
import React from "react";
import { ActivityIndicator, Pressable, StyleProp, StyleSheet, TextStyle, TouchableOpacity, View } from "react-native";
import moment from "moment";

import { tRepRetailerConnectionResponse, tVisualConnectionStatus } from "tmt_api/src/types/types.validator";
import * as mappers from "tmt_api/src/shared/mappers";

import { useAppDispatch } from "src/redux";
import * as utilsDb from "src/db/utils.db";
import * as connectionDb from "src/db/connection.db";
import { PdStack, PdTag, PdTagLabel, PdText } from "src/components/layout";
import {
  PdTable,
  PdTableCell,
  PdTableCellText,
  PdTableMobileItemSeparator,
  PdTablePaginationFooter,
  PdTableRow,
  PdTableRowMobile,
  PdTableSortableHeaders,
} from "src/components/v2/layout/table";
import { tColumn, tRowInstance, useTable } from "src/hooks/table";
import { useDebounce, useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";

import { kRoute } from "src/kRoute";
import { useSelector } from "react-redux";
import { tPressableState } from "src/components/types";
import { PdSearchInput } from "src/components/v2/ui";
import { PdButton, PdButtonLabel } from "src/components/v2/forms/button";
import { PdMessage, PdStorefront, PdUserAdd } from "src/components/Icons";
import { formatDatabaseConnectionStatus } from "tmt_api/src/util/formatters";
import { kTablePageSize } from "src/util/constants";
import { useAddRetailerModal, useInviteRetailersModal } from "src/hooks/modals";
import { PdIconButton } from "src/components/forms/icon-button";
import { setCurrentThread } from "src/redux/messages/actions";
import {
  changeFilterConnectionStatus,
  changeFilterNextPage,
  changeFilterPrevPage,
  changeFilterTerm,
  resetFilters,
} from "src/redux/filters/actions";
import { PdBadge, PdBadgeLabel, PdTab, PdTabLabel, tPdBadgeColorScheme } from "src/components/v2/layout";
import { PdFormLabel } from "src/components/v2/forms/control";

type tTableState = {
  data: tRepRetailerConnectionResponse[];
  isLoading: boolean;
  prev: string | null;
  next: string | null;
};

const getColorSchemeFromConnectionStatus = (status: tVisualConnectionStatus): tPdBadgeColorScheme => {
  if (status === "Declined") return "red";
  if (status === "Pending") return "yellow";
  if (status === "Invited") return "orange";
  return "green";
};

const MessageCell: React.FC<{ threadId: string; style?: StyleProp<TextStyle> }> = ({ threadId, style }) => {
  const kDispatch = useAppDispatch();
  const kNav = useNavigation();

  const handleOnPress = () => {
    kDispatch(setCurrentThread(threadId));
    kNav.navigate(kRoute.P04);
  };

  return (
    <PdIconButton
      contentContainerStyle={styles.messageButton}
      onPress={handleOnPress}
      size="sm"
      variant="ghost"
      icon={<PdMessage style={styles.messageIcon} />}
      style={style}
    />
  );
};

const ConnectionMobileRow: React.FC<{
  item: tRowInstance<tRepRetailerConnectionResponse>;
  onPress?: (item: tRowInstance<tRepRetailerConnectionResponse>) => void;
}> = ({ item, onPress }) => {
  const handleOnPress = () => {
    onPress?.(item);
  };

  return (
    <TouchableOpacity onPress={handleOnPress}>
      <PdTableRowMobile style={styles.mobileRow}>
        <View style={styles.rowMobileContainer}>
          <PdText style={styles.orderMobileRowOrderNumber}>{item.retailerName}</PdText>
        </View>
        <PdBadge colorScheme={getColorSchemeFromConnectionStatus(item.status)}>
          <PdBadgeLabel>{item.status}</PdBadgeLabel>
        </PdBadge>
      </PdTableRowMobile>
    </TouchableOpacity>
  );
};

const ConnectionRow: React.FC<{
  item: tRowInstance<tRepRetailerConnectionResponse>;
  onPress?: (item: tRowInstance<tRepRetailerConnectionResponse>) => void;
}> = ({ item, onPress }) => {
  const handleOnPress = () => {
    onPress?.(item);
  };

  return (
    <Pressable onPress={handleOnPress}>
      {({ pressed, hovered }: tPressableState) => (
        <PdTableRow style={[hovered ? styles.rowHovered : {}, pressed ? styles.rowPressed : {}]}>
          {item.cells.map((cell) => (
            <PdTableCell key={cell.uniqueId} style={cell.style}>
              {cell.render("Cell")}
            </PdTableCell>
          ))}
        </PdTableRow>
      )}
    </Pressable>
  );
};

const P18_RepMyRetailers: React.FC<{ navigation: NavigationProp<ParamListBase> }> = ({ navigation }) => {
  const isFocused = useIsFocused();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const inviteRetailersModal = useInviteRetailersModal();
  const addRetailerModal = useAddRetailerModal();
  const filters = useSelector((state) => state.FiltersReducer.repMyRetailers);
  const dTermFilter = useDebounce(filters.term, 500);
  const [tableState, setTableState] = React.useState<tTableState>({
    isLoading: true,
    data: [],
    prev: null,
    next: null,
  });

  const columns: tColumn<tRepRetailerConnectionResponse>[] = React.useMemo(
    () => [
      {
        id: "id",
        header: () => null,
        accessor: (originalRow) => originalRow.id,
        Cell: ({ row }) => {
          if (
            (row.status === "Active" || row.status === "Approved") &&
            moment()
              .startOf("day")
              .hour(12)
              .diff(moment(new Date(row.createdAtInMillis)), "days") <= 14
          ) {
            return (
              <PdTag style={{ backgroundColor: kColors.brandGreen }}>
                <PdTagLabel style={{ color: kColors.white }}>NEW!</PdTagLabel>
              </PdTag>
            );
          }

          return null;
        },
        isSortable: false,
        style: [styles.tableColumnXs],
      },
      {
        id: "retailerName",
        header: "Retailer Name",
        accessor: (originalRow) => originalRow.retailerName,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnLg,
      },
      {
        id: "retailerCategory",
        header: "Retailer Category",
        accessor: (originalRow) => originalRow.retailerCategory || "-",
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnLg,
      },
      {
        id: "retailerPricePoint",
        header: "Price Point",
        accessor: (originalRow) => originalRow.retailerPricePoint || "-",
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "status",
        header: "Status",
        accessor: (originalRow) => originalRow.status,
        Cell: ({ value, row }) => (
          <PdBadge colorScheme={getColorSchemeFromConnectionStatus(row.status)}>
            <PdBadgeLabel>{value}</PdBadgeLabel>
          </PdBadge>
        ),
        style: styles.tableColumnSm,
      },
      {
        id: "message-button",
        header: "Message",
        accessor: (originalRow) => originalRow.id,
        Cell: ({ row, style }) =>
          row.conversationId ? <MessageCell threadId={row.conversationId} style={style} /> : null,
        style: [{ alignItems: "center", justifyContent: "center" }, styles.tableColumnSm],
        isSortable: false,
      },
    ],
    [],
  );

  const tableInstance = useTable<tRepRetailerConnectionResponse>({
    data: tableState.data,
    columns,
    initialState: {
      sortedBy: "createdAtInMillis",
      sortDirection: "desc",
    },
  });

  const handleOnPressRow = React.useCallback(
    (row: tRowInstance<tRepRetailerConnectionResponse>) => {
      if (row.status === "Active") {
        navigation.navigate(kRoute.P10, { id: row.retailerId, connectionId: row.id });
      }
    },
    [navigation],
  );

  const handleOnPressInvite = async () => {
    inviteRetailersModal.openAsync({ companyId: rCompanyId, source: "rep" });
  };

  const handleOnPressAddRetailer = () => {
    addRetailerModal.openAsync({ companyId: rCompanyId, source: "rep" });
  };

  const handleOnPressPrevPage = () => {
    dispatch(changeFilterPrevPage({ pageName: "repMyRetailers", value: tableState?.prev ?? "" }));
  };

  const handleOnPressNextPage = () => {
    dispatch(changeFilterNextPage({ pageName: "repMyRetailers", value: tableState?.next ?? "" }));
  };

  const renderItem: React.FC<{ item: tRowInstance<tRepRetailerConnectionResponse> }> = React.useCallback(
    ({ item }) => {
      if (isMobile) return <ConnectionMobileRow item={item} onPress={handleOnPressRow} />;
      return <ConnectionRow item={item} onPress={handleOnPressRow} />;
    },
    [handleOnPressRow, isMobile],
  );

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const query = await utilsDb.withPagination(
        connectionDb.queryRepRetailerConnection,
        connectionDb.documentRepRetailerConnection,
        {
          term: dTermFilter,
          status: filters.connectionStatus ? formatDatabaseConnectionStatus(filters.connectionStatus) : undefined,
          repId: rCompanyId,
          prev: filters.prev,
          next: filters.next,
          limit: kTablePageSize,
        },
      );

      unsubscribe = query.onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => mappers.mapRepRetailerConnection(doc.data()));
        setTableState({
          isLoading: false,
          data,
          prev: data[0]?.id ?? "",
          next: data.length >= kTablePageSize ? data[data.length - 1]?.id ?? "" : "",
        });
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, [dTermFilter, filters.connectionStatus, filters.next, filters.prev, rCompanyId]);

  React.useEffect(() => {
    if (!isFocused) {
      dispatch(resetFilters());
    }
  }, [dispatch, isFocused]);

  if (tableState.isLoading) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandGreen} />
      </View>
    );
  }

  return (
    <View style={isMobile ? styles.mobilePage : styles.page}>
      <PdStack direction={isMobile ? "vertical" : "horizontal"} style={[styles.justifyBetween, styles.mb4]}>
        <PdText variant="subheader" style={isMobile ? styles.mb4 : {}}>
          My Retailers
        </PdText>
        <PdStack direction={isMobile ? "vertical" : "horizontal"}>
          <PdButton
            colorScheme="purple"
            leftIcon={<PdUserAdd color="black" style={styles.buttonIcon} />}
            onPress={handleOnPressInvite}
          >
            <PdButtonLabel>Invite your Retailers</PdButtonLabel>
          </PdButton>
          <PdButton
            colorScheme="pink"
            onPress={handleOnPressAddRetailer}
            leftIcon={<PdStorefront style={styles.buttonIcon} />}
          >
            <PdButtonLabel>Add a Retailer</PdButtonLabel>
          </PdButton>
        </PdStack>
      </PdStack>
      <View style={styles.mb4}>
        <PdFormLabel>Search your retailers</PdFormLabel>
        <PdSearchInput
          value={filters.term}
          onChangeText={(value) => dispatch(changeFilterTerm({ pageName: "repMyRetailers", value }))}
          placeholder="Search"
        />
      </View>
      <View style={styles.filtersStatusContainer}>
        <PdTab
          isActive={!filters.connectionStatus}
          onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "connections" }))}
        >
          <PdTabLabel>All</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.connectionStatus === "Active"}
          onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "connections", value: "Active" }))}
        >
          <PdTabLabel>Active</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.connectionStatus === "Pending"}
          onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "connections", value: "Pending" }))}
        >
          <PdTabLabel>Pending</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.connectionStatus === "Invited"}
          onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "connections", value: "Invited" }))}
        >
          <PdTabLabel>Invited</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.connectionStatus === "Declined"}
          onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "connections", value: "Declined" }))}
        >
          <PdTabLabel>Declined</PdTabLabel>
        </PdTab>
      </View>
      <PdTable
        ListHeaderComponent={!isMobile ? <PdTableSortableHeaders headers={tableInstance.headers} /> : null}
        ItemSeparatorComponent={() => (isMobile ? <PdTableMobileItemSeparator /> : null)}
        data={tableInstance.rows}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        isMobile={isMobile}
      />
      <PdTablePaginationFooter
        page={filters.page}
        hasNext={!!tableState?.next}
        onPressBack={handleOnPressPrevPage}
        onPressNext={handleOnPressNextPage}
      />
    </View>
  );
};

export const P18 = React.memo(P18_RepMyRetailers);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  buttonIcon: {
    height: 14,
    width: 14,
  },
  filtersStatusContainer: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 2 * kStyleConst.kSpacing,
    marginBottom: kStyleConst.kSpacing * 2,
  },
  flex1: {
    flex: 1,
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  messageButton: {
    height: 34,
    marginVertical: -kStyleConst.kSpacing * 2,
    width: 34,
  },
  messageIcon: {
    width: 20,
  },
  mobilePage: {
    flex: 1,
    paddingBottom: kStyleConst.kSpacing * 2,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingTop: kStyleConst.kSpacing * 4,
  },
  mobileRow: {
    alignItems: "center",
    flexDirection: "row",
    padding: kStyleConst.kSpacing * 2,
  },
  orderMobileRowOrderNumber: {
    fontSize: 16,
    fontWeight: "500",
  },
  page: {
    flex: 1,
    paddingBottom: kStyleConst.kSpacing * 4,
    paddingHorizontal: kStyleConst.kSpacing * 10,
    paddingTop: kStyleConst.kSpacing * 8,
  },
  rowHovered: {
    backgroundColor: kColors.gray50,
  },
  rowMobileContainer: {
    flex: 1,
  },
  rowPressed: {
    opacity: 0.6,
  },
  tableColumnLg: {
    width: 300,
  },
  tableColumnMd: {
    width: 160,
  },
  tableColumnSm: {
    width: 100,
  },
  tableColumnXs: {
    width: 50,
  },
});
